import { useState, useEffect } from 'react';

type TeamKind = 'black' | 'white';

interface JoinCelebrationProps {
  team: TeamKind | null;
  onDone: () => void;
}

const COLORS = ['#d4a843', '#f5d080', '#ffd700', '#ffe066', '#ffffff', '#f0c040', '#e8b84b'];
const PARTICLE_COUNT = 44;

// Build particles once at module level (stable between renders)
const PARTICLES = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
  const angle = (i / PARTICLE_COUNT) * Math.PI * 2 + (((i * 137 + 19) % 100) / 100 - 0.5) * 0.4;
  const dist = 90 + ((i * 73 + 11) % 170);
  return {
    id: i,
    dx: `${Math.cos(angle) * dist}px`,
    dy: `${Math.sin(angle) * dist}px`,
    size: 3 + (i % 6),
    color: COLORS[i % COLORS.length],
    delay: `${((i * 31 + 7) % 45) / 100}s`,
    duration: `${0.9 + ((i * 17 + 3) % 8) / 10}s`,
  };
});

export default function JoinCelebration({ team, onDone }: JoinCelebrationProps) {
  const [phase, setPhase] = useState<'in' | 'hold' | 'out'>('in');

  useEffect(() => {
    const t0 = setTimeout(() => setPhase('hold'), 60);
    const t1 = setTimeout(() => {
      setPhase('out');
      // Signal MemberProfileModal to start sliding in (crossfade timing)
      window.dispatchEvent(new CustomEvent('srs-join-done'));
    }, 2100);
    const t2 = setTimeout(onDone, 2850);
    return () => { clearTimeout(t0); clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);

  const isBlack = team === 'black';
  const teamLabel = isBlack ? '黑隊' : '白隊';
  const teamColor = isBlack ? '#d4a843' : '#e0e0e0';
  const teamGlow = isBlack ? 'rgba(212,168,67,0.5)' : 'rgba(220,220,220,0.4)';
  const teamIcon = isBlack ? 'ri-shield-fill' : 'ri-sword-fill';

  const opacity = phase === 'hold' ? 1 : 0;
  const transition = phase === 'in' ? 'opacity 0.35s ease-in' : 'opacity 0.75s ease-out';

  return (
    <div
      className="fixed inset-0 flex items-center justify-center overflow-hidden pointer-events-none"
      style={{
        background: 'radial-gradient(ellipse at center, #110e00 0%, #020202 100%)',
        opacity,
        transition,
        zIndex: 90,
      }}
    >
      {/* Expanding rings */}
      {[1, 2, 3, 4].map(i => (
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            width: `${i * 160}px`,
            height: `${i * 160}px`,
            border: `1px solid rgba(212,168,67,${0.35 - i * 0.07})`,
            animation: `jc-ring-expand 2s ease-out ${i * 0.14}s both`,
          }}
        />
      ))}

      {/* Radial rays */}
      {Array.from({ length: 10 }, (_, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            width: '1.5px',
            height: '0px',
            background: `linear-gradient(to top, ${teamColor}cc, transparent)`,
            bottom: '50%',
            left: 'calc(50% - 0.75px)',
            transformOrigin: 'bottom center',
            transform: `rotate(${i * 36}deg)`,
            animation: `jc-ray-shoot 1.3s ease-out 0.08s both`,
          }}
        />
      ))}

      {/* Burst particles */}
      {PARTICLES.map(p => (
        <div
          key={p.id}
          className="absolute rounded-full"
          style={{
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: p.color,
            boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
            top: '50%',
            left: '50%',
            '--dx': p.dx,
            '--dy': p.dy,
            animation: `jc-particle ${p.duration} ${p.delay} ease-out both`,
          } as React.CSSProperties}
        />
      ))}

      {/* Central glow orb */}
      <div
        className="absolute rounded-full"
        style={{
          width: '180px',
          height: '180px',
          background: `radial-gradient(circle, ${teamGlow} 0%, transparent 70%)`,
          filter: 'blur(28px)',
          animation: 'jc-orb 0.7s ease-out 0.05s both',
        }}
      />

      {/* Corner sparks */}
      {['top-6 left-6', 'top-6 right-6', 'bottom-6 left-6', 'bottom-6 right-6'].map((pos, idx) => (
        <div
          key={pos}
          className={`absolute ${pos}`}
          style={{ animation: `jc-corner 1s ease-out ${0.5 + idx * 0.08}s both` }}
        >
          <i
            className="ri-sparkling-2-fill text-2xl"
            style={{ color: teamColor, filter: `drop-shadow(0 0 8px ${teamColor})` }}
          />
        </div>
      ))}

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center gap-3 text-center px-6">

        {/* Team icon */}
        <div
          className="w-16 h-16 flex items-center justify-center rounded-full mb-1"
          style={{
            border: `2px solid ${teamColor}`,
            background: `${teamColor}18`,
            boxShadow: `0 0 24px ${teamColor}50`,
            animation: 'jc-icon 0.55s cubic-bezier(0.22,1,0.36,1) 0.12s both',
          }}
        >
          <i className={`${teamIcon} text-3xl`} style={{ color: teamColor, filter: `drop-shadow(0 0 8px ${teamColor})` }} />
        </div>

        {/* Title */}
        <div style={{ animation: 'jc-title 0.55s cubic-bezier(0.22,1,0.36,1) 0.22s both' }}>
          <p className="text-[#555] text-[10px] tracking-[0.45em] uppercase mb-1.5">Welcome</p>
          <p
            className="font-black leading-tight"
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: 'clamp(30px, 8vw, 48px)',
              color: teamColor,
              textShadow: `0 0 20px ${teamColor}99, 0 0 50px ${teamColor}44`,
            }}
          >
            歡迎加入{teamLabel}
          </p>
        </div>

        {/* Subtitle */}
        <p
          className="text-[#777] text-sm tracking-[0.25em]"
          style={{ animation: 'jc-sub 0.45s ease-out 0.42s both' }}
        >
          ＊ 你的榮耀之路，正式展開 ＊
        </p>

        {/* Divider */}
        <div
          className="flex items-center gap-3"
          style={{ animation: 'jc-sub 0.45s ease-out 0.55s both' }}
        >
          <div className="h-px w-14" style={{ background: `linear-gradient(90deg, transparent, ${teamColor}70)` }} />
          <i className="ri-crown-fill text-sm" style={{ color: `${teamColor}99`, filter: `drop-shadow(0 0 6px ${teamColor})` }} />
          <div className="h-px w-14" style={{ background: `linear-gradient(90deg, ${teamColor}70, transparent)` }} />
        </div>
      </div>

      <style>{`
        @keyframes jc-ring-expand {
          from { transform: scale(0); opacity: 0.9; }
          to   { transform: scale(4); opacity: 0; }
        }
        @keyframes jc-ray-shoot {
          from { height: 0; opacity: 1; }
          50%  { height: 200px; opacity: 0.8; }
          to   { height: 300px; opacity: 0; }
        }
        @keyframes jc-orb {
          from { transform: scale(0); opacity: 0; }
          50%  { transform: scale(2.2); opacity: 1; }
          to   { transform: scale(1.3); opacity: 0.5; }
        }
        @keyframes jc-particle {
          from { transform: translate(-50%,-50%) scale(1.8); opacity: 1; }
          80%  { opacity: 0.7; }
          to   { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0.1); opacity: 0; }
        }
        @keyframes jc-icon {
          from { transform: scale(0) rotate(-120deg); opacity: 0; }
          to   { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes jc-title {
          from { transform: scale(1.6) translateY(-12px); opacity: 0; filter: blur(8px); }
          to   { transform: scale(1) translateY(0); opacity: 1; filter: blur(0); }
        }
        @keyframes jc-sub {
          from { transform: translateY(14px); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
        @keyframes jc-corner {
          from { transform: scale(0) rotate(-30deg); opacity: 0; }
          45%  { transform: scale(1.5) rotate(15deg); opacity: 1; }
          to   { transform: scale(1) rotate(0deg); opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}
